import { DsButton, DsIconButton, DsModal } from '@frontends/ds';
import { UiKitDataCell } from '@frontends/ui-kit';
import { SymbolIcon } from '@frontends/util-symbol-icons';
import { createCurrencyFormatter, formatSignedMoney, formatSignedPercent } from '@frontends/util-utils';
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { selectAccount } from '../../../features/initialSlice/initialSlice';
import {
  resetSession,
  selectBalance,
  selectIsFinishReached,
  selectSessionStatistic,
} from '../../../features/mainSlice/mainSlice';
import { useAppDispatch, useAppSelector } from '../../../hooks/hooks';
import { Confetti } from '../../confetti/confetti';
import { EquityCurve, EquityPoint } from './equityCurve';
import { ModalLogos } from './modalLogos';
import { ShareSessionStatistic } from './share';
import styles from './sessionResultModal.module.scss';

export const SessionResultModal = (): React.JSX.Element | null => {
  const dispatch = useAppDispatch();
  const { t, i18n } = useTranslation();

  const isFinishReached = useAppSelector(selectIsFinishReached);
  const stats = useAppSelector(selectSessionStatistic);
  const initialBalance = useAppSelector(selectBalance);
  const account = useAppSelector(selectAccount);

  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (isFinishReached && stats !== null) {
      setOpen(true);
    }
  }, [isFinishReached, stats]);
  const variant: 'profit' | 'loss' = (stats?.totalPNL ?? 0) >= 0 ? 'profit' : 'loss';

  const onClose = useCallback(() => {
    setOpen(false);
  }, []);

  const onNewSession = useCallback(() => {
    dispatch(resetSession());
  }, [dispatch]);

  const shareContainerRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  const currency = account?.currency ?? 'USD';
  const moneyFmt = useMemo(
    () =>
      createCurrencyFormatter(currency, i18n.language || 'en', {
        maximumFractionDigits: 2,
      }),
    [i18n.language, currency],
  );

  const points: EquityPoint[] = useMemo(() => {
    if (!stats) return [];

    return stats.equityTimeline.map((point) => ({
      time: point.time,
      pnl: point.equity - initialBalance,
    }));
  }, [stats, initialBalance]);

  const durationUnits: DurationUnits = useMemo(
    () => ({
      month: t('sessionStatistic.modal.durationUnits.month'),
      week: t('sessionStatistic.modal.durationUnits.week'),
      day: t('sessionStatistic.modal.durationUnits.day'),
      hour: t('sessionStatistic.modal.durationUnits.hour'),
      minute: t('sessionStatistic.modal.durationUnits.minute'),
    }),
    [t],
  );

  if (!stats) return null;

  const showConfetti = open && variant === 'profit' && stats.totalTrades >= 1;

  return (
    <>
      <DsModal isOpen={open} onOpenChange={setOpen} aria-labelledby={titleId}>
        <div className={`${styles.shell} ${styles[variant]}`}>
          <div className={styles.halo} />

          <div className={styles.modal}>
            <div id="share-container" className="bg-backgroundRaised" ref={shareContainerRef}>
              <header>
                <div className={styles.iconWrap}>
                  <div className={styles.icon}>{variant === 'profit' ? <ProfitIcon /> : <LossIcon />}</div>
                  <div className={styles.titleCol}>
                    {stats.selectedAsset ? (
                      <div className={`${styles.symbol} gap-2 flex items-center`} title={stats.selectedAsset.name}>
                        <SymbolIcon logoUrls={stats.selectedAsset.logo_urls} symbol={stats.selectedAsset.name} />
                        {stats.selectedAsset.name}
                      </div>
                    ) : null}
                    <div id={titleId} className={styles.tag}>
                      {t(
                        variant === 'profit'
                          ? 'sessionStatistic.modal.completeProfit'
                          : 'sessionStatistic.modal.completeLoss',
                      )}
                    </div>
                  </div>
                </div>
                <DsIconButton
                  id="close-modal-button"
                  data-share-exclude="true"
                  variant="secondary"
                  size="sm"
                  name="x"
                  onPress={onClose}
                  aria-label={t('sessionStatistic.modal.close')}
                />
              </header>

              <div className={styles.pnlSection}>
                <div className={styles.pnlAmount}>{formatSignedMoney(stats.totalPNL, moneyFmt)}</div>
                <div className={styles.pnlSub}>
                  <span className={styles.pnlPct}>{formatSignedPercent(stats.totalPNLPercent)}</span>
                  <span className={styles.pnlVs}>{t('sessionStatistic.modal.vsInitialBalance')}</span>
                  <span className={styles.pnlInitial}>{moneyFmt.format(initialBalance)}</span>
                </div>
              </div>

              <div className={styles.statsSection}>
                <div className={styles.statsGrid}>
                  <UiKitDataCell
                    containerClassName={styles.statCell}
                    label={t('sessionStatistic.modal.trades')}
                    value={String(stats.totalTrades)}
                  />
                  <UiKitDataCell
                    containerClassName={styles.statCell}
                    label={t('sessionStatistic.modal.winRate')}
                    value={`${Math.round(stats.winRate * 100)}%`}
                  />
                  <UiKitDataCell
                    containerClassName={styles.statCell}
                    label={t('sessionStatistic.modal.bestTrade')}
                    value={formatSignedMoney(stats.bestTrade, moneyFmt)}
                    tone={stats.bestTrade >= 0 ? 'positive' : 'negative'}
                  />
                  <UiKitDataCell
                    containerClassName={styles.statCell}
                    label={t('sessionStatistic.modal.worstTrade')}
                    value={formatSignedMoney(stats.worstTrade, moneyFmt)}
                    tone={stats.worstTrade >= 0 ? 'positive' : 'negative'}
                  />
                  <UiKitDataCell
                    containerClassName={styles.statCell}
                    label={t('sessionStatistic.modal.avgTrade')}
                    value={formatSignedMoney(stats.avgTrade, moneyFmt)}
                    tone={variant === 'profit' ? 'positive' : 'negative'}
                  />
                  <UiKitDataCell
                    containerClassName={styles.statCell}
                    label={t('sessionStatistic.modal.duration')}
                    value={formatDuration(stats.duration, durationUnits)}
                  />
                </div>
              </div>

              <div className={styles.chartSection}>
                <div className={styles.chartLabel}>{t('sessionStatistic.modal.equityCurve')}</div>
                <EquityCurve points={points} variant={variant} />
              </div>

              <div className={styles.divider} />

              <div className="flex justify-center items-center gap-2 p-5">
                <div data-share-exclude="true" className="mr-auto">
                  <ShareSessionStatistic ref={shareContainerRef} />
                </div>

                <ModalLogos />
              </div>
            </div>

            <footer className="flex gap-2 p-5">
              <DsButton
                variant={variant === 'profit' ? 'primary' : 'destructive'}
                size="md"
                className="flex-[2]"
                iconStart={variant === 'profit' ? 'check' : 'rotate-ccw'}
                onPress={onNewSession}
              >
                {t(variant === 'profit' ? 'sessionStatistic.modal.newSession' : 'sessionStatistic.modal.tryAgain')}
              </DsButton>
              <DsButton variant="secondary" size="md" className="flex-1" onPress={onClose}>
                {t('sessionStatistic.modal.close')}
              </DsButton>
            </footer>
          </div>
        </div>
      </DsModal>
      <Confetti active={showConfetti} />
    </>
  );
};

type DurationUnits = {
  month: string;
  week: string;
  day: string;
  hour: string;
  minute: string;
};

function formatDuration(seconds: number, units: DurationUnits): string {
  if (!seconds || seconds < 0) return '—';

  const MIN = 60;
  const HOUR = 3600;
  const DAY = 86400;
  const WEEK = 7 * DAY;
  const MONTH = 30 * DAY;

  const months = Math.floor(seconds / MONTH);
  const weeks = Math.floor((seconds % MONTH) / WEEK);
  const days = Math.floor((seconds % WEEK) / DAY);
  const hours = Math.floor((seconds % DAY) / HOUR);
  const minutes = Math.floor((seconds % HOUR) / MIN);

  if (months > 0) return weeks > 0 ? `${months}${units.month} ${weeks}${units.week}` : `${months}${units.month}`;

  if (weeks > 0) return days > 0 ? `${weeks}${units.week} ${days}${units.day}` : `${weeks}${units.week}`;

  if (days > 0) return hours > 0 ? `${days}${units.day} ${hours}${units.hour}` : `${days}${units.day}`;

  if (hours > 0) return minutes > 0 ? `${hours}${units.hour} ${minutes}${units.minute}` : `${hours}${units.hour}`;

  return `${minutes}${units.minute}`;
}

const ProfitIcon = (): React.JSX.Element => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path
      d="M4 14L8 9L11 12L16 6"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M13 6H16V9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const LossIcon = (): React.JSX.Element => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path
      d="M4 6L8 11L11 8L16 14"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M13 14H16V11" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
